import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeftIcon, CloudArrowUpIcon, XMarkIcon } from '@heroicons/react/24/outline';
import WhatsAppDevicePreview from './WhatsAppDevicePreview';
import WhatsAppTemplateCategoryStep from './WhatsAppTemplateCategoryStep';
import WhatsAppTemplateDraftFields from './WhatsAppTemplateDraftFields';
import WhatsAppTemplateMappingsSection from './WhatsAppTemplateMappingsSection';
import { devicePreviewFromTemplate, templateHeaderMimeType } from './whatsappTemplatePreview';
import type { WhatsAppTemplate } from '../../models/MasterAdmin/whatsappCampaign';
import {
  categoryLabel,
  optOutFooter,
  resolvedTemplateButtons,
  templateTypeLabel,
  type TemplateFormState,
} from './whatsappTemplateForm';

export type WhatsAppTemplateWizardMode = 'create' | 'edit';

interface WhatsAppTemplateCreateWizardProps {
  open: boolean;
  mode?: WhatsAppTemplateWizardMode;
  form: TemplateFormState;
  setForm: React.Dispatch<React.SetStateAction<TemplateFormState>>;
  onClose: () => void;
  onDraft: () => void | Promise<void>;
  onPublish: () => void | Promise<void>;
  saving?: boolean;
  editTemplate?: WhatsAppTemplate | null;
  publishLabel?: string;
  showDraftButton?: boolean;
}

export const WhatsAppTemplateCreateWizard: React.FC<WhatsAppTemplateCreateWizardProps> = ({
  open,
  mode = 'create',
  form,
  setForm,
  onClose,
  onDraft,
  onPublish,
  saving = false,
  editTemplate,
  publishLabel,
  showDraftButton = true,
}) => {
  const [step, setStep] = useState<1 | 2>(mode === 'edit' ? 2 : 1);

  useEffect(() => {
    if (open) setStep(mode === 'edit' ? 2 : 1);
  }, [open, mode]);

  const previewProps = useMemo(() => {
    const draft: WhatsAppTemplate = {
      id: editTemplate?.id || 'draft',
      name: form.name || 'new_template',
      displayName: form.displayName,
      category: form.category,
      languageCode: form.languageCode,
      bodyPreview: form.bodyPreview,
      headerText: form.headerFormat === 'TEXT' ? form.headerText : null,
      headerFormat: form.headerFormat,
      variableCount: form.variableCount,
      footer: optOutFooter(form) || null,
      buttons: resolvedTemplateButtons(form),
    };
    return devicePreviewFromTemplate(draft, {
      headerMediaUrl: form.headerSampleUrl,
      headerMimeType: templateHeaderMimeType(form.headerFormat),
    });
  }, [form, editTemplate?.id]);

  if (!open) return null;

  const title = mode === 'edit' ? `Edit template — ${editTemplate?.name || form.name}` : 'New template';

  return (
    <div className="fixed inset-0 z-[110] flex items-end justify-center bg-black/50 p-0 sm:items-stretch sm:justify-stretch sm:p-0">
      <div className="flex h-[96vh] w-full max-w-none flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl sm:h-full sm:rounded-none dark:bg-gray-900">
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-200 px-4 py-3 sm:px-6 dark:border-gray-700">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              {step === 2 && mode === 'create' && (
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300"
                >
                  <ArrowLeftIcon className="h-3.5 w-3.5" />
                  Back
                </button>
              )}
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h2>
            </div>
            {step === 2 && (
              <p className="mt-1 inline-flex rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                {categoryLabel(form.category)} · {templateTypeLabel(form.templateType)}
              </p>
            )}
            {step === 1 && (
              <p className="mt-1 text-sm text-gray-500">Step 1 of 2 — choose category and template type</p>
            )}
            {step === 2 && (
              <p className="mt-1 text-sm text-gray-500">
                Step 2 of 2 — draft your template. Preview updates live on the right.
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden lg:flex-row">
          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6">
            {step === 1 ? (
              <WhatsAppTemplateCategoryStep
                form={form}
                setForm={setForm}
                onContinue={() => setStep(2)}
              />
            ) : (
              <div className="mx-auto max-w-3xl space-y-6 pb-8">
                <WhatsAppTemplateDraftFields
                  form={form}
                  setForm={setForm}
                  nameReadOnly={false}
                />
                <WhatsAppTemplateMappingsSection
                  bodyPreview={form.bodyPreview}
                  mappings={form.defaultVariableMappings}
                  onMappingsChange={(defaultVariableMappings) =>
                    setForm((prev) => ({ ...prev, defaultVariableMappings }))
                  }
                />
              </div>
            )}
          </div>

          {step === 2 && (
            <aside className="hidden w-[320px] shrink-0 flex-col border-l border-gray-200 bg-gray-50/80 p-4 dark:border-gray-700 dark:bg-gray-950/40 lg:flex xl:w-[360px]">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Live preview</p>
              <div className="sticky top-4 flex flex-col items-center">
                <WhatsAppDevicePreview
                  message={previewProps.message}
                  headerText={previewProps.headerText}
                  footer={previewProps.footer}
                  buttons={previewProps.buttons}
                  headerMediaUrl={previewProps.headerMediaUrl}
                  headerMimeType={previewProps.headerMimeType}
                  contactName="Vedika.Health"
                />
              </div>
            </aside>
          )}
        </div>

        {step === 2 && (
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-gray-200 px-4 py-3 sm:px-6 dark:border-gray-700">
            <p className="text-xs text-gray-500 lg:hidden">Scroll up to edit fields · preview on desktop</p>
            <div className="ml-auto flex flex-wrap gap-2">
              {showDraftButton && (
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => void onDraft()}
                  className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-60 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
                >
                  {saving ? 'Saving...' : 'Save draft'}
                </button>
              )}
              <button
                type="button"
                disabled={saving}
                onClick={() => void onPublish()}
                className="inline-flex items-center gap-2 rounded-lg bg-[#25D366] px-4 py-2 text-sm font-semibold text-white hover:bg-[#1da851] disabled:opacity-60"
              >
                <CloudArrowUpIcon className="h-4 w-4" />
                {saving ? 'Publishing...' : publishLabel || 'Publish to Interakt'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsAppTemplateCreateWizard;
